// 旧后台路径 → /admin 前缀的兼容重定向（保留 query/hash，老书签与历史硬编码跳转不失效）
const ADMIN_LEGACY_PATHS = [
    'dashboard', 'users', 'agents', 'merchants', 'recharges',
    'ai-generate', 'model-config', 'publish', 'platforms', 'accounts',
    'knowledge', 'keywords', 'data', 'roles', 'logs', 'settings', 'profile'
];
const adminLegacyRedirects = ADMIN_LEGACY_PATHS.map(p => ({
    path: '/' + p,
    redirect: to => ({ path: '/admin' + to.path, query: to.query, hash: to.hash })
}));

export const routes = [
    // ===== 官网（公开，无需登录） =====
    {
        path: '/',
        name: 'SiteLayout',
        component: () => import('@/views/site/SiteLayout.vue'),
        meta: { requiresAuth: false },
        children: [
            {
                path: '',
                name: 'SiteHome',
                component: () => import('@/views/site/SiteHome.vue'),
                meta: { requiresAuth: false, seo: { title: '优臣GEO - 让 AI 搜索为你的门店获取客资', description: '面向全国生活服务商家的 AI 搜索优化（GEO）平台：AI 内容生成、30+ 自媒体平台一键发布、关键词收录监测，抢占 AI 答案中的品牌位置。' } }
            },
            {
                path: 'news',
                name: 'SiteNews',
                component: () => import('@/views/site/SiteNews.vue'),
                meta: { requiresAuth: false, seo: { title: '新闻资讯 - 优臣GEO', description: 'GEO 行业动态、AI 搜索优化实战方法与优臣GEO 产品更新。' } }
            }
        ]
    },
    {
        path: '/login',
        name: 'Login',
        component: () => import('@/views/login/Login.vue'),
        meta: { requiresAuth: false }
    },
    // 商户使用指南：独立公开页，无需登录（登录页与后台侧栏均可进入）
    {
        path: '/guide',
        name: 'MerchantGuide',
        component: () => import('@/views/guide/MerchantGuide.vue'),
        meta: { requiresAuth: false, seo: { title: '商户使用指南 - 优臣GEO', description: '优臣GEO 商户后台使用指南：平台账号授权、知识库、关键词、AI 内容生成、发布任务与收录监测的完整操作流程。' } }
    },
    // GEO 运营实战指南：独立公开页，无需登录（GEO 方法论与落地打法）
    {
        path: '/geo',
        name: 'GeoOpsGuide',
        component: () => import('@/views/guide/GeoOpsGuide.vue'),
        meta: { requiresAuth: false, seo: { title: 'GEO 运营实战指南 - 优臣GEO', description: '面向生活服务商家的前沿 GEO 方法论：AI 搜索 RAG 原理、核心指标体系、内容策略、平台矩阵、关键词五层模型、监测迭代与 90 天落地路线图。' } }
    },
    // ===== 管理后台（需登录，统一 /admin 前缀） =====
    {
        path: '/admin',
        name: 'Layout',
        component: () => import('@/components/Layout/AppLayout.vue'),
        redirect: '/admin/dashboard',
        children: [
            {
                path: 'dashboard',
                name: 'Dashboard',
                component: () => import('@/views/dashboard/Dashboard.vue'),
                meta: { title: '首页', icon: 'HomeFilled', permission: 'data:dashboard' }
            },
            {
                path: 'users',
                name: 'UserList',
                component: () => import('@/views/user/UserList.vue'),
                meta: { title: '用户管理', icon: 'UserFilled', permission: 'user:list', group: 'org' }
            },
            {
                path: 'agents',
                name: 'AgentList',
                component: () => import('@/views/agent/AgentList.vue'),
                meta: { title: '代理商管理', icon: 'OfficeBuilding', permission: 'agent:list', group: 'org' }
            },
            {
                path: 'merchants',
                name: 'MerchantList',
                component: () => import('@/views/merchant/MerchantList.vue'),
                meta: { title: '商户管理', icon: 'Shop', permission: 'merchant:list', group: 'org' }
            },
            {
                path: 'recharges',
                name: 'RechargeList',
                component: () => import('@/views/merchant/RechargeList.vue'),
                meta: { title: '充值与账单', icon: 'Money', permission: 'merchant:list', group: 'org' }
            },
            {
                path: 'model-config',
                name: 'ModelConfig',
                component: () => import('@/views/ai/ModelConfig.vue'),
                meta: { title: 'AI模型配置', icon: 'SetUp', permission: 'ai:model_config', group: 'system' }
            },
            {
                path: 'publish',
                name: 'PublishList',
                component: () => import('@/views/publish/PublishList.vue'),
                meta: { title: '发布任务', icon: 'Promotion', permission: 'publish:list', group: 'distribute' }
            },
            {
                path: 'publish-stats',
                name: 'PublishStats',
                component: () => import('@/views/publish/PublishStats.vue'),
                // publish:stats 是给代理商开的独立权限（他们看统计但不碰发布任务）
                meta: { title: '发布统计', icon: 'DataAnalysis', permission: ['publish:list', 'publish:stats'], group: 'distribute' }
            },
            {
                path: 'platforms',
                name: 'PlatformList',
                component: () => import('@/views/platform/PlatformList.vue'),
                meta: { title: '媒体平台', icon: 'Grid', permission: 'platform:manage', group: 'system' }
            },
            {
                path: 'accounts',
                name: 'AccountList',
                component: () => import('@/views/account/AccountList.vue'),
                // 仅超管与商户可见（代理商不碰账号）；接口层已按当前用户过滤，只能看自己的账号
                meta: { title: '平台管理', icon: 'User', permission: 'publish:list', userTypes: [1, 3], group: 'distribute' }
            },
            {
                path: 'knowledge',
                name: 'KnowledgeList',
                component: () => import('@/views/knowledge/KnowledgeList.vue'),
                meta: { title: '知识库', icon: 'Collection', permission: 'knowledge:list', group: 'content' }
            },
            {
                path: 'keywords',
                name: 'KeywordList',
                component: () => import('@/views/keyword/KeywordList.vue'),
                meta: { title: '关键词', icon: 'Key', permission: 'keyword:list', group: 'content' }
            },
            {
                path: 'ai-generate',
                name: 'AIGenerate',
                component: () => import('@/views/ai/AIGenerate.vue'),
                meta: { title: 'AI内容生成', icon: 'ChatDotRound', permission: 'ai:generate', group: 'content' }
            },
            {
                path: 'data',
                name: 'DataDashboard',
                component: () => import('@/views/data/DataDashboard.vue'),
                meta: { title: '数据中心', icon: 'TrendCharts', permission: 'data:dashboard', group: 'monitor' }
            },
            {
                path: 'news-manage',
                name: 'NewsManage',
                component: () => import('@/views/news/NewsManage.vue'),
                meta: { title: '资讯管理', icon: 'Notebook', superAdmin: true, group: 'system' }
            },
            {
                path: 'roles',
                name: 'RoleList',
                component: () => import('@/views/role/RoleList.vue'),
                meta: { title: '角色管理', icon: 'Lock', permission: 'role:list', group: 'org' }
            },
            {
                path: 'logs',
                name: 'LogList',
                component: () => import('@/views/log/LogList.vue'),
                meta: { title: '操作日志', icon: 'Document', superAdmin: true, group: 'system' }
            },
            {
                path: 'settings',
                name: 'SystemConfig',
                component: () => import('@/views/settings/SystemConfig.vue'),
                meta: { title: '系统配置', icon: 'Setting', permission: 'system:config', group: 'system' }
            },
            {
                path: 'profile',
                name: 'Profile',
                component: () => import('@/views/profile/Profile.vue'),
                meta: { title: '个人中心', hidden: true }
            }
        ]
    },
    // 旧后台路径兼容重定向（必须在 404 之前）
    ...adminLegacyRedirects,
    {
        path: '/403',
        name: 'Forbidden',
        component: () => import('@/views/error/403.vue'),
        meta: { requiresAuth: false }
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: () => import('@/views/error/404.vue'),
        meta: { requiresAuth: false }
    }
];
